import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Globe, Calendar, Clock, Users, Video, Heart, 
  MessageCircle, Star, Award, CheckCircle, Sparkles,
  ChevronLeft, ChevronRight, X, Copy, ExternalLink, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";
import { useAuth } from "../context/AuthContext";

// 격주 이벤트 생성 함수
const generateBiweeklyEvents = () => {
  const events = [];

  // 격주 시리즈를 앵커 날짜 기준으로 자동 생성 (7월 및 이후 달까지 자동 채움)
  const addSeries = (anchor, count, type, title, time, color, prefix) => {
    for (let i = 0; i < count; i++) {
      const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + 14 * i);
      events.push({
        id: `${prefix}-${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`,
        day: d.getDate(),
        month: d.getMonth(),
        year: d.getFullYear(),
        type,
        title,
        time,
        color,
      });
    }
  };

  // 토요 줌미팅: 2026-03-28(토) 기준 격주 토요일
  addSeries(new Date(2026, 2, 28), 60, "saturday", "토요 줌미팅", "오전 10:30", "bg-amber-500", "sat");
  // 목요 줌미팅: 2026-04-02(목) 기준 격주 목요일
  addSeries(new Date(2026, 3, 2), 60, "thursday", "목요 줌미팅", "오후 9:00", "bg-purple-500", "thu");

  return events;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GlobalZoomMeetingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [activeTab, setActiveTab] = useState("thursday");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("");
  
  // 줌미팅 신청 관련 state
  const [registering, setRegistering] = useState(false);
  const [myRegistration, setMyRegistration] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  const isMember = isAuthenticated && user && ['regular', 'premium', 'vip'].includes(user.membership_type);
  
  const events = useMemo(() => generateBiweeklyEvents(), []);

  // 참가자 목록 + 내 신청 여부 조회
  const fetchEventData = useCallback(async (eventId) => {
    setLoadingParticipants(true);
    try {
      // 참가자 목록 (공개)
      const pRes = await fetch(`${API_URL}/api/zoom-meetings/${eventId}/participants`);
      if (pRes.ok) {
        const pData = await pRes.json();
        setParticipants(pData.participants || []);
      }
      // 내 신청 여부
      if (isAuthenticated && token) {
        const mRes = await fetch(`${API_URL}/api/zoom-meetings/${eventId}/my-registration`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mRes.ok) {
          const mData = await mRes.json();
          setMyRegistration(mData.registered ? mData.registration : null);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingParticipants(false);
    }
  }, [isAuthenticated, token]);
  
  // 이벤트 클릭 핸들러
  const handleEventClick = (event) => {
    setMyRegistration(null);
    setParticipants([]);
    setSelectedAgeGroup("");
    setCopiedCode(false);
    
    if (!isAuthenticated) {
      setSelectedEvent(event);
      setShowMembershipModal(true);
      return;
    }
    if (!isMember) {
      setSelectedEvent(event);
      setShowMembershipModal(true);
      return;
    }
    setSelectedEvent(event);
    fetchEventData(event.id);
  };

  // 줌미팅 신청
  const handleRegister = async () => {
    if (!selectedAgeGroup) {
      alert("연령대를 선택해주세요.");
      return;
    }
    setRegistering(true);
    try {
      const res = await fetch(`${API_URL}/api/zoom-meetings/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_id: selectedEvent.id,
          age_group: selectedAgeGroup,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMyRegistration({
          zoom_link: data.zoom_link,
          zoom_code: data.zoom_code,
          age_group: selectedAgeGroup,
          username: user?.username,
        });
        // 참가자 목록 새로고침
        fetchEventData(selectedEvent.id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRegistering(false);
    }
  };

  const copyZoomCode = () => {
    if (myRegistration?.zoom_code) {
      navigator.clipboard.writeText(myRegistration.zoom_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // 달력 데이터 생성
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    
    const days = [];
    
    // 이전 달의 날짜들
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i)
      });
    }
    
    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day: day,
        isCurrentMonth: true,
        date: new Date(currentYear, currentMonth, day)
      });
    }
    
    // 다음 달의 날짜들 (6주 채우기)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day: day,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth + 1, day)
      });
    }
    
    return days;
  }, [currentYear, currentMonth]);
  
  const getEventForDay = (day, month, year) => {
    return events.find(e => 
      e.day === day && 
      e.month === month && 
      e.year === year
    );
  };
  
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];

  const scheduleData = {
    thursday: {
      day: "목요일",
      time: "오후 9시",
      icon: "🌙",
      sessions: [
        { time: "9:00 PM - 10:00 PM", ageGroup: "40~50대", desc: "첫 1시간" },
        { time: "10:00 PM - 11:00 PM", ageGroup: "50~60대", desc: "후반 1시간" },
      ]
    },
    saturday: {
      day: "토요일",
      time: "오전 10시 30분",
      icon: "☀️",
      sessions: [
        { time: "10:30 AM - 11:30 AM", ageGroup: "40~50대", desc: "첫 1시간" },
        { time: "11:30 AM - 12:30 PM", ageGroup: "50~60대", desc: "후반 1시간" },
      ]
    }
  };

  const features = [
    { icon: Globe, title: "글로벌 참여", desc: "전 세계 한인들과 함께" },
    { icon: Users, title: "1:1, 2:2, 3:3", desc: "다양한 소그룹 대화" },
    { icon: Heart, title: "연애 코칭", desc: "전문가의 연애 기술 전수" },
    { icon: MessageCircle, title: "편안한 대화", desc: "부담 없는 자유로운 소통" },
  ];

  const benefits = [
    "전 세계 어디서든 참여 가능",
    "정회원 무료 참여 혜택",
    "연령대별 맞춤 시간 배정",
    "1:1부터 3:3까지 다양한 대화 형식",
    "전문 MC의 연애 코칭 제공",
    "매칭 성공 시 후속 연결 지원",
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-stone-900">
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_d8c908f3-2c65-4297-9403-0f3522c6f55b/artifacts/09vddxw7_image.png')`,
            backgroundSize: '110% auto',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            transform: 'scale(1.05)',
          }}
        />
        {/* Neutral dark overlay (no green tint) for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Globe Animation */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-10">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="w-full h-full border-2 border-white/30 rounded-full"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            className="absolute inset-10 border border-white/20 rounded-full"
          />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 border border-white/30 rounded-full text-white text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              Global Korean Community
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-[720] text-white mb-6">
              글로벌 줌미팅
              <span className="block text-emerald-400 mt-2">전 세계 한인과 함께</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-4">
              다양한 사람들과 편안하게 대화를 나누며<br />
              나와 잘 맞는 특별한 인연을 찾아가는 시간
            </p>

            {/* 강조 문구 */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full text-white font-bold text-lg mb-8 shadow-lg"
            >
              <Award className="w-5 h-5 text-white" />
              정회원을 위한 특별한 온라인 만남과 소통의 플랫폼
            </motion.div>

            {/* Info Cards */}
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {[
                { icon: Calendar, label: "일정", value: "격주 목/토" },
                { icon: Clock, label: "시간", value: "목 9PM / 토 10:30AM (KST)" },
                { icon: Users, label: "형식", value: "1:1, 2:2, 3:3" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-white" />
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{item.label}</p>
                      <p className="text-white font-semibold text-sm">{item.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Schedule Section */}

      {/* Calendar Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-emerald-500 font-medium text-sm tracking-wider uppercase">Calendar</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              줌미팅 일정표
            </h2>
            <p className="text-stone-500 mt-2">격주로 진행됩니다 (한국시간 기준)</p>
          </motion.div>

          {/* Calendar */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-stone-200">
            {/* Calendar Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPrevMonth}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <h3 className="text-2xl font-bold text-white">
                  {currentYear}년 {monthNames[currentMonth]}
                </h3>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
              {dayNames.map((day, index) => (
                <div
                  key={day}
                  className={`py-3 text-center text-sm font-semibold ${
                    index === 0 ? "text-red-500" : index === 6 ? "text-blue-500" : "text-stone-600"
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => {
                const event = dayInfo.isCurrentMonth 
                  ? getEventForDay(dayInfo.day, currentMonth, currentYear) 
                  : null;
                const isToday = dayInfo.isCurrentMonth && 
                  dayInfo.day === new Date().getDate() && 
                  currentMonth === new Date().getMonth() &&
                  currentYear === new Date().getFullYear();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[90px] p-2 border-b border-r border-stone-100 ${
                      !dayInfo.isCurrentMonth ? "bg-stone-50" : "bg-white"
                    } ${event ? "cursor-pointer hover:bg-emerald-50 transition-colors" : ""}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      !dayInfo.isCurrentMonth ? "text-stone-300" : 
                      index % 7 === 0 ? "text-red-500" : 
                      index % 7 === 6 ? "text-blue-500" : "text-stone-700"
                    }`}>
                      <span className={`${isToday ? "bg-emerald-500 text-white w-7 h-7 rounded-full inline-flex items-center justify-center" : ""}`}>
                        {dayInfo.day}
                      </span>
                    </div>
                    
                    {event && (
                      <div className="relative group">
                        <motion.div
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          onClick={() => handleEventClick(event)}
                          className={`${event.color} rounded-lg p-2 text-white text-xs cursor-pointer hover:opacity-90 transition-opacity`}
                        >
                          <p className="font-bold">{event.title}</p>
                          <p className="opacity-80 text-[10px]">{event.time}</p>
                        </motion.div>
                        {/* Hover Popup */}
                        <div className="absolute left-full top-0 ml-2 z-50 hidden group-hover:block w-56">
                          <div className="bg-white rounded-xl shadow-xl border border-stone-200 p-4 text-left">
                            <p className="font-bold text-stone-800 text-sm mb-2">{event.title}</p>
                            <div className="space-y-1.5 text-xs text-stone-600">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-stone-400" />
                                <span>{event.year}년 {event.month + 1}월 {event.day}일 ({event.type === 'saturday' ? '토' : '목'})</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-stone-400" />
                                <span>{event.time} (KST)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-stone-400" />
                                <span>40~50대 / 50~60대</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Video className="w-3.5 h-3.5 text-stone-400" />
                                <span>1:1, 2:2, 3:3 소그룹 대화</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                              className="mt-2 w-full py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors"
                              data-testid={`popup-apply-btn-${event.id}`}
                            >
                              신청하기
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded"></div>
              <span className="text-sm text-stone-600">목요일 (오후 9시)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500 rounded"></div>
              <span className="text-sm text-stone-600">토요일 (오전 10:30)</span>
            </div>
          </div>
          <p className="text-center text-stone-500 text-sm mt-4">* 일정을 클릭하면 신청할 수 있습니다 (모든 시간은 한국시간 KST 기준)</p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-emerald-500 font-medium text-sm tracking-wider uppercase">About</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              글로벌 줌미팅이란?
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-emerald-100"
          >
            <div className="space-y-6 text-stone-700 leading-relaxed">
              <p className="text-lg">
                <span className="text-emerald-600 font-semibold">글로벌 줌미팅</span>은 
                전 세계에 계신 한인 중년분들을 위한 특별한 온라인 소통 프로그램입니다.
                미국, 캐나다, 호주, 유럽 등 어디서든 참여할 수 있습니다.
              </p>
              
              <p>
                <span className="text-emerald-600 font-semibold">1:1, 2:2, 3:3</span> 다양한 형식의 
                소그룹 대화를 통해 부담 없이 새로운 인연을 만들어보세요.
                전문 MC가 진행하는 <span className="text-emerald-600 font-semibold">연애 기술과 코칭</span>도 
                함께 제공됩니다.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                  <h4 className="text-emerald-600 font-bold mb-2">🌍 참여 자격</h4>
                  <p className="text-sm text-stone-600">New Coupling 정회원 이상<br/>(정회원 가입 후 참여 가능)</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                  <h4 className="text-emerald-600 font-bold mb-2">⏰ 진행 일정</h4>
                  <p className="text-sm text-stone-600">격주 목요일 오후 9시 (KST)<br/>격주 토요일 오전 10시 30분 (KST)</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                  <h4 className="text-emerald-600 font-bold mb-2">👥 연령대별 시간</h4>
                  <p className="text-sm text-stone-600">첫 1시간: 40~50대<br/>후반 1시간: 50~60대</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                  <h4 className="text-emerald-600 font-bold mb-2">💬 진행 방식</h4>
                  <p className="text-sm text-stone-600">1:1, 2:2, 3:3 소그룹 대화<br/>+ 연애 기술 코칭</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-6">
                <p className="text-amber-800 text-sm">
                  <strong>💡 참고:</strong> 글로벌 줌미팅은 정회원 전용 프로그램입니다.
                  정회원 가입 후 일정에 맞춰 Zoom 링크를 받아 참여하실 수 있습니다.
                  모든 시간은 한국시간(KST) 기준입니다.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-emerald-500 font-medium text-sm tracking-wider uppercase">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              참여 혜택
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl"
              >
                <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                <span className="text-stone-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-white mb-4">
              지금 바로 참여하세요
            </h2>
            <p className="text-emerald-100 text-lg mb-8">
              정회원 가입 후 글로벌 줌미팅에 참여하실 수 있습니다
            </p>
            <Button
              onClick={() => navigate('/membership')}
              className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg font-bold rounded-xl shadow-lg"
            >
              정회원 가입하기
            </Button>
          </motion.div>
        </div>
      </section>

      <FooterSection />

      {/* Event Detail + Register Modal */}
      <AnimatePresence>
        {selectedEvent && !showMembershipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`${selectedEvent.color} p-6 text-white relative`}>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-testid="close-event-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Video className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{selectedEvent.title}</h3>
                    <p className="text-white/80">
                      {selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 ({selectedEvent.type === 'saturday' ? '토' : '목'})
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Event Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-700">{selectedEvent.time} (한국시간)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-700">Zoom 온라인 (전 세계 어디서든 참여)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-700">40~50대 / 50~60대</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-stone-400" />
                    <span className="text-emerald-600 font-semibold">정회원 전용</span>
                  </div>
                </div>

                {/* 미신청 상태: 신청 폼 */}
                {!myRegistration && (
                  <>
                    <div className="bg-stone-50 rounded-xl p-4 mb-5">
                      <h4 className="font-semibold text-stone-800 mb-3">진행 내용</h4>
                      <ul className="space-y-2 text-sm text-stone-600">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          1:1, 2:2, 3:3 소그룹 편안한 대화
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          전문 MC의 연애 기술 코칭
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          매칭 서비스 제공
                        </li>
                      </ul>
                    </div>

                    {/* 연령대 선택 */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-stone-700 mb-2">연령대 선택 <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-3 gap-2">
                        {['40대', '50대', '60대'].map((age) => (
                          <button
                            key={age}
                            onClick={() => setSelectedAgeGroup(age)}
                            className={`py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                              selectedAgeGroup === age
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                            }`}
                            data-testid={`age-group-${age}`}
                          >
                            {age}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={handleRegister}
                      disabled={!selectedAgeGroup || registering}
                      className={`w-full py-4 font-bold text-lg rounded-xl ${
                        selectedAgeGroup
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white'
                          : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                      }`}
                      data-testid="zoom-register-btn"
                    >
                      {registering ? (
                        <><Loader2 className="w-5 h-5 animate-spin mr-2" /> 신청 중...</>
                      ) : (
                        "줌미팅 신청하기"
                      )}
                    </Button>
                  </>
                )}

                {/* 신청 완료 상태: 줌코드 + 링크 */}
                {myRegistration && (
                  <>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-5" data-testid="zoom-code-section">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        <span className="font-bold text-emerald-700">참가 신청 완료</span>
                      </div>

                      <div className="bg-white rounded-lg p-4 mb-3">
                        <p className="text-xs text-stone-500 mb-1">Zoom 미팅 코드</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-stone-800 font-mono tracking-wider">{myRegistration.zoom_code}</span>
                          <button
                            onClick={copyZoomCode}
                            className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
                            data-testid="copy-zoom-code"
                          >
                            {copiedCode ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <Copy className="w-5 h-5 text-stone-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      <a
                        href={myRegistration.zoom_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
                        data-testid="zoom-join-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Zoom 미팅 입장하기
                      </a>
                    </div>
                  </>
                )}

                {/* 참가 신청자 명단 (항상 표시) */}
                <div className="border border-stone-200 rounded-xl p-4" data-testid="participants-list">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-stone-800 flex items-center gap-2">
                      <Users className="w-4 h-4 text-stone-500" />
                      참가 신청자 명단
                    </h4>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                      {participants.length}명
                    </span>
                  </div>

                  {loadingParticipants ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    </div>
                  ) : participants.length === 0 ? (
                    <p className="text-sm text-stone-400 text-center py-4">아직 신청자가 없습니다. 첫 번째 참가자가 되어보세요!</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {participants.map((p, idx) => (
                        <div key={p.id || idx} className="flex items-center gap-3 p-2 rounded-lg bg-stone-50">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                            p.membership_type === 'vip' ? 'bg-purple-400' :
                            p.membership_type === 'premium' ? 'bg-amber-400' : 'bg-emerald-400'
                          }`}>
                            {(p.username || '?')[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-stone-700 truncate block">{p.username}</span>
                          </div>
                          <span className="text-xs text-stone-400 flex-shrink-0">{p.age_group}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Membership Required Modal */}
      <AnimatePresence>
        {showMembershipModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => {
              setShowMembershipModal(false);
              setSelectedEvent(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-amber-500" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-2">정회원 전용</h3>
                <p className="text-stone-600 mb-6">
                  글로벌 줌미팅은 <span className="font-semibold text-emerald-600">정회원</span>만 참여하실 수 있습니다.
                </p>
                
                <div className="bg-stone-100 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-stone-500 mb-1">선택하신 일정</p>
                  <p className="font-semibold text-stone-800">
                    {selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 ({selectedEvent.type === 'saturday' ? '토' : '목'}) {selectedEvent.time}
                  </p>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                  <p className="text-emerald-700 text-sm">
                    <strong>💎 정회원 혜택</strong><br />
                    • 글로벌 줌미팅 참여<br />
                    • 연애 기술 코칭 제공<br />
                    • 매칭 서비스 우선 제공
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => {
                      setShowMembershipModal(false);
                      setSelectedEvent(null);
                      navigate('/membership');
                    }}
                    className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white font-bold rounded-xl"
                  >
                    정회원 가입하기
                  </Button>
                  <Button
                    onClick={() => {
                      setShowMembershipModal(false);
                      setSelectedEvent(null);
                    }}
                    variant="outline"
                    className="w-full py-4 border-stone-300 text-stone-600 hover:bg-stone-50 rounded-xl"
                  >
                    다음에 할게요
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
